import * as Tabs from '@wandb/weave/components/Tabs';
import React, {useContext, useState} from 'react';
import {useHistory} from 'react-router';

import {useViewer} from '../../state/viewer/hooks';
import * as urls from '../../util/urls';
import {SelectEntity} from '../Launch/Queues/SelectEntity';
import {CreateTriggerDrawer} from '../Launch/Triggers/CreateTriggerDrawer/CreateTriggerDrawer';
import {getTeamModelRegistryTriggerDrawerInit} from '../Launch/Triggers/CreateTriggerDrawer/EventStore';
import {TriggersTable} from '../Launch/Triggers/TriggersTable/TriggersTable';
import {ZeroTriggers} from '../Launch/Triggers/ZeroTriggers';
import {CollectionsTab} from '../Registries/common/CollectionsTab';
import {useIsProjectReadOnly} from '../Registries/common/hooks';
import {MigrationStatusBanner} from '../Registries/ModelRegistryMigration/MigrationStatusBanner';
import {ModelRegistryMigrationContext} from '../Registries/ModelRegistryMigration/ModelRegistryMigrationContextProvider';
import {MigrationStatus} from '../Registries/ModelRegistryMigration/utils';
import {CreateRegisteredCollectionDrawer} from '../Registries/workflows/CreateRegisteredCollectionDrawer/CreateRegisteredCollectionDrawer';
import {
  useAllModelRegistryArtifactCollectionsWithoutMembers,
  useSearchablePaginatedModelRegistryArtifactCollections,
  useTeamTriggers,
} from './hooks';
import * as S from './ModelRegistryPage.styles';
import {ModelRegistryPageHeader} from './ModelRegistryPageHeader';
import {
  AUTOMATIONS_TAB_NAME,
  COLLECTIONS_TAB_NAME,
  DEFAULT_PROJECT_NAME,
} from './utils';

const MODEL_REGISTRY_PAGE_SIZE = 10;

type SingleTeamModelRegistryPageProps = {
  entityName: string;
};
export const SingleTeamModelRegistryPage = ({
  entityName,
}: SingleTeamModelRegistryPageProps) => {
  // Model Registry Migration Context
  const {entityMigrationStatus: MRMigrationStatus} = useContext(
    ModelRegistryMigrationContext
  );

  const [selectedTab, setSelectedTab] = useState(COLLECTIONS_TAB_NAME);

  const artifactType = 'model';
  const history = useHistory();
  const viewer = useViewer();
  const [showNewPortfolioModal, setShowNewPortfolioModal] = useState(false);
  const [showNewAutomationModal, setShowNewAutomationModal] = useState(false);
  const username = viewer?.username;
  const isPersonalEntity = entityName === username;

  const {triggers, projectTriggersQuery} = useTeamTriggers(entityName);

  // User changed entity filter selection
  const onSelectEntity = (name: string) => {
    const url = name
      ? urls.objectRegistry('model', undefined, name)
      : urls.objectRegistry('model', undefined);
    history.push(url);
  };

  // FIXME: Suboptimal workaround to preserve behavior of CreateRegisteredCollectionDrawer,
  // which expects a list of existing collections to check for conflicts
  const {
    collections: existingCollections,
    refetch: refetchExistingCollections,
  } = useAllModelRegistryArtifactCollectionsWithoutMembers({
    entityName,
  });

  // These are the collections to be displayed in the collections tab
  const {
    currentPage: currentPaginatedCollections,
    loading: collectionsLoading,
    refetch: refetchCollections,
    hasNextPage,
    totalCollectionsLoaded,
    page,
    onPageChange,
  } = useSearchablePaginatedModelRegistryArtifactCollections({
    entityName,
    pageSize: MODEL_REGISTRY_PAGE_SIZE,
  });

  const {isProjectReadOnly: readOnly} = useIsProjectReadOnly(
    entityName,
    DEFAULT_PROJECT_NAME
  );
  const totalCollectionCount = existingCollections.length;

  // Upon completion of the migration, we want to hide all model registry content and
  // only display the team selector with the banner.
  if (MRMigrationStatus === MigrationStatus.COMPLETED) {
    return (
      <S.Panel>
        <S.OptionsPanel>
          {viewer != null && (
            <S.SelectPanel>
              <SelectEntity
                size="large"
                value={entityName}
                onSelectEntity={onSelectEntity}
                includeAll
              />
            </S.SelectPanel>
          )}
        </S.OptionsPanel>
        <MigrationStatusBanner
          className="mb-24"
          entityName={entityName}
          projectName={DEFAULT_PROJECT_NAME}
        />
      </S.Panel>
    );
  }
  return (
    <>
      <S.Panel>
        <ModelRegistryPageHeader
          onCreateModel={() => {
            setShowNewPortfolioModal(true);
          }}
          isPersonalEntity={isPersonalEntity}
          entityName={entityName}
          onCreateAutomation={() => {
            setShowNewAutomationModal(true);
          }}
        />
        <S.OptionsPanel>
          {/*This page is accessible in the anon state since we don't always route via the homepage. To avoid confusion
          since the team-list will always be empty for anon users, it would be better to hide this dropdown*/}
          {viewer != null && (
            <S.SelectPanel>
              <SelectEntity
                size="large"
                value={entityName}
                onSelectEntity={onSelectEntity}
                includeAll
              />
            </S.SelectPanel>
          )}
        </S.OptionsPanel>
        <MigrationStatusBanner
          className="mb-24"
          entityName={entityName}
          projectName={DEFAULT_PROJECT_NAME}
        />
        <Tabs.Root
          value={selectedTab}
          onValueChange={value => setSelectedTab(value)}>
          <Tabs.List>
            <Tabs.Trigger value={COLLECTIONS_TAB_NAME}>
              Collections
            </Tabs.Trigger>
            <Tabs.Trigger value={AUTOMATIONS_TAB_NAME}>
              Automations
            </Tabs.Trigger>
          </Tabs.List>
          <div className="pt-16">
            <Tabs.Content value={COLLECTIONS_TAB_NAME}>
              <CollectionsTab
                totalCount={totalCollectionCount}
                currentPaginatedCollections={currentPaginatedCollections}
                refetchCollections={refetchCollections}
                setShowNewCollectionModal={setShowNewPortfolioModal}
                collectionsLoading={collectionsLoading}
                totalCollectionsLoaded={totalCollectionsLoaded}
                page={page}
                pageSize={MODEL_REGISTRY_PAGE_SIZE}
                hasNextPage={hasNextPage}
                onPageChange={onPageChange}
              />
            </Tabs.Content>
            <Tabs.Content value={AUTOMATIONS_TAB_NAME}>
              <AutomationsTab
                {...{
                  triggers,
                  projectTriggersQuery,
                  entityName,
                  readOnly,
                }}
              />
            </Tabs.Content>
          </div>
        </Tabs.Root>
      </S.Panel>
      <CreateRegisteredCollectionDrawer
        open={showNewPortfolioModal}
        closeDrawer={() => setShowNewPortfolioModal(false)}
        artifactTypeName={artifactType}
        registryEntityName={entityName}
        defaultProjectName={DEFAULT_PROJECT_NAME}
        existingCollections={existingCollections}
        refetchCollections={refetchExistingCollections}
      />
      <CreateTriggerDrawer
        open={showNewAutomationModal}
        closeDrawer={() => {
          setShowNewAutomationModal(false);
        }}
        refetchProjectTriggers={projectTriggersQuery.refetch}
        createTriggerDrawerInit={{
          ...getTeamModelRegistryTriggerDrawerInit(entityName),
          isUpdating: false,
          trigger: undefined,
        }}
      />
    </>
  );
};

type AutomationsTabProps = {
  // TODO: types
  triggers: any[];
  projectTriggersQuery: any;
  entityName: string;
  readOnly: boolean;
};
const AutomationsTab = ({
  triggers,
  projectTriggersQuery,
  entityName,
  readOnly,
}: AutomationsTabProps) => {
  return (
    <>
      {triggers.length === 0 ? (
        <ZeroTriggers />
      ) : (
        <TriggersTable
          projectTriggersQuery={projectTriggersQuery}
          baseTriggerDrawerInit={getTeamModelRegistryTriggerDrawerInit(
            entityName
          )}
          readOnly={readOnly}
        />
      )}
    </>
  );
};
